import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  GetAppsResponse,
  deleteData,
  getApps,
  getConnectorTypes,
  getConnectors,
  getHome,
  getRoles,
  ApiTokenSource,
  setApiTokenSource,
} from "./api";
import { getPages } from "./api/page";
import { deleteTable } from "./api/table";
import { useAppIdMap } from "./hook/app";
import { useCreateConversation } from "./hook/chat";
import {
  useFetchNotebook,
  useNotebook,
  useSelectMainNote as useSelectMainNotebookPage,
  useSelectedNotebookPageId,
} from "./hook/page";
import {
  useConnectorTypes,
  useHandleConnectorsResponse,
} from "./hook/pullConnector";
import { useFetchPushConnectors } from "./hook/pushConnector";
import { useHandleGetRolesResponse } from "./hook/role";
import { useFetchUserSettings } from "./hook/setting";
import { useSubscriptionStatus } from "./hook/settings";
import {
  ActionType,
  AppDispatch,
  PageContentType,
  RootState,
  SelectedPage,
} from "./reducer";
import { App } from "./types/app";
import { homeAppOf } from "./types/home";
import { TableSpecMap } from "./types/table";

export const useAppId: () => [
  string | undefined,
  (notebook: string) => void
] = () => {
  const appId = useSelector((state: RootState) => state.ui.appId);
  const dispatch = useDispatch<AppDispatch>();
  const setNotebook = (appId: string) =>
    dispatch({ type: ActionType.SET_APP_ID, appId });
  return [appId, setNotebook];
};

export const useApps: () => [App[], (apps: App[]) => void] = () => {
  const appId = useSelector((state: RootState) => state.settings.apps);
  const dispatch = useDispatch<AppDispatch>();
  const setNotebook = (apps: App[]) =>
    dispatch({ type: ActionType.SET_APPS, apps });
  return [appId, setNotebook];
};

// The Account's Home Workspace (state.settings.homeApp), read from the Home Noun (system:#home), never from get-apps.
export const useHomeApp: () => [string | undefined, (homeApp: string | undefined) => void] = () => {
  const homeApp = useSelector((state: RootState) => state.settings.homeApp);
  const dispatch = useDispatch<AppDispatch>();
  const setHomeApp = (homeApp: string | undefined) =>
    dispatch({ type: ActionType.SET_HOME_APP, homeApp });
  return [homeApp, setHomeApp];
};

// Read the caller's Home Record and store its "app-id" ("" -> undefined: no Home to badge or pin).
export const useFetchHomeApp: () => () => Promise<void> = () => {
  const [, setHomeApp] = useHomeApp();
  return () => getHome().then((record) => setHomeApp(homeAppOf(record)));
};

export const useSelectedPage: () => [
  SelectedPage,
  (selectedPage: SelectedPage) => void
] = () => {
  const selectedPage = useSelector(
    (state: RootState) => state.ui.selectedPage
  );
  const dispatch = useDispatch<AppDispatch>();
  const setSelectedPage = (selectedPage: SelectedPage) =>
    dispatch({ type: ActionType.SET_SELECTED_PAGE, selectedPage });
  return [selectedPage, setSelectedPage];
};

export const useNoWorkspaces: () => [
  boolean,
  (noWorkspaces: boolean) => void
] = () => {
  const noWorkspaces = useSelector(
    (state: RootState) => state.ui.noWorkspaces ?? false
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const setNoWorkspaces = (noWorkspaces: boolean) => {
    dispatch({
      type: ActionType.SET_NO_WORKSPACES,
      noWorkspaces,
    });
    if (noWorkspaces) {
      navigate(`/no-workspace`);
    }
  };
  return [noWorkspaces, setNoWorkspaces];
};

export const useAppOwner: () => [
  string | undefined,
  (appOwner: string | undefined) => void
] = () => {
  const appOwner = useSelector((state: RootState) => state.settings.appOwner);
  const dispatch = useDispatch<AppDispatch>();
  const setAppOwner = (appOwner: string | undefined) =>
    dispatch({ type: ActionType.SET_APP_OWNER, appOwner });
  return [appOwner, setAppOwner];
};

export const useFetchAppId: () => () => Promise<void> = () => {
  const { appId, setAppId } = useAppIdMap();
  const [authTokenMaybe, setAuthToken] = useAuthToken();
  const [selectedPage, setSelectedPage] = useSelectedPage();
  const [noWorkspaces, setNoWorkspaces] = useNoWorkspaces();
  const [appOwner, setAppOwner] = useAppOwner();
  const [apps, setApps] = useApps();
  const fetchHomeApp = useFetchHomeApp();
  const authToken = authTokenMaybe!;

  const setAppFromResponse = (response: GetAppsResponse) => {
    const selectedApp = response["selected-app"]
      ? response.apps.find((app: App) => app.id === response["selected-app"])
      : undefined;
    const selectedAppId = selectedApp?.id;
    const appOwner = selectedApp?.["owner-email"];
    setAppOwner(appOwner);
    const apps = response.apps;
    setApps(apps);
    if (selectedAppId && appId === undefined) setAppId(selectedAppId);
    if (!selectedAppId) setNoWorkspaces(true);
  };

  return async () => {
    getApps({ authToken }).then(setAppFromResponse);
    fetchHomeApp();
  };
};

export const useTablePage: (config: {
  tableId?: string;
  index: number;
}) => string[][] | undefined = ({ tableId, index }) => {
  const tablePages = useSelector((state: RootState) =>
    tableId ? state.tables.tablePages[tableId]?.[index] : undefined
  );
  return tablePages;
};

export const useSetTableSpecs: () => (
  tableSpecs: TableSpecMap
) => void = () => {
  const dispatch = useDispatch();
  const setTableSpecs = (tableSpecs: TableSpecMap) => {
    dispatch({ type: ActionType.SET_TABLE_SPECS, tableSpecs });
  };
  return setTableSpecs;
};

export const useFetchAppDataListener: () => void = () => {
  const [appId, setAppId] = useAppId();
  const [connectorTypes, setConnectorTypes] = useConnectorTypes();
  const handleConnectorsResponse = useHandleConnectorsResponse();
  const createConversation = useCreateConversation();
  const fetchPushConnectors = useFetchPushConnectors();
  const selectMainNotebookPage = useSelectMainNotebookPage();
  const [selectedPage, setSelectedPage] = useSelectedPage();
  const handleGetRolesResponse = useHandleGetRolesResponse();
  const { user } = useAuth0();
  const { hasAccess, loading } = useSubscriptionStatus();
  const fetchNotebook = useFetchNotebook();
  const email = user?.email;
  const [selectedNotebookPageId, setSelectedNotebookPageId] =
    useSelectedNotebookPageId();
  const fetchUserSettings = useFetchUserSettings();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (loading) return;

    if (!hasAccess) {
      navigate("/settings");
    }
  }, [loading, hasAccess]);

  useEffect(() => {
    if (!appId || !hasAccess || !email) return;
    fetchUserSettings();
    fetchNotebook().then((notebook) => {
      if (pathname === "/") {
        navigate(`/newchat`);
      }
    });
    getRoles({ email }).then(handleGetRolesResponse);
    // getConnectors({ appId }).then((connectorsResponse) => {
    //   handleConnectorsResponse(connectorsResponse);
    // });
    // createConversation();
    // getConnectorTypes().then((x) => {
    //   setConnectorTypes(x);
    //   // setSelectedPage({
    //   //   contentType: PageContentType.SELECT_CONNECT_CARD_TYPE,
    //   // });
    // });
    // fetchPushConnectors();
  }, [appId, hasAccess, email]);
};

export const useFetchAppListener: () => void = () => {
  const [authToken] = useAuthToken();
  const [appId, setAppId] = useAppId();
  const fetchAppId = useFetchAppId();
  // Load the account's apps once a token first exists, not on every renewal: the API Token Source
  // (useFetchAuthToken) replaces state.ui.authToken about once a day, and legacy get-apps resolves its token
  // with an UNCACHED Auth0 /userinfo call, the rate-limited endpoint (query-omega-api pages/api/auth0-cache.html).
  const hasAuthToken = !!authToken;
  useEffect(() => {
    if (!hasAuthToken) return;
    fetchAppId();
  }, [hasAuthToken]);

  useFetchAppDataListener();
};

export const useDeleteNoteTable: () => (config: {
  tableId: string;
}) => void = () => {
  const [notebook, setNotebook] = useNotebook();
  const [appId, setAppId] = useAppId();
  const selectMainNote = useSelectMainNotebookPage();

  const deleteNoteTable: (config: { tableId: string }) => void = ({
    tableId,
  }) => {
    deleteTable({ tableId })
      .then((response) => getPages({ "app-id": appId! }))
      .then((notebook) => {
        setNotebook(notebook);
        selectMainNote({ notebook });
      });
  };
  return deleteNoteTable;
};

export const useAuthToken: () => [
  string | undefined,
  (authToken: string) => void
] = () => {
  const authToken = useSelector((state: RootState) => state.ui.authToken);
  const dispatch = useDispatch();
  const setAuthToken = (authToken: string) => {
    dispatch({ type: ActionType.SET_AUTH_TOKEN, authToken });
  };
  return [authToken, setAuthToken];
};

export const useFetchAuthToken: () => void = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [, setAuthToken] = useAuthToken();
  useEffect(() => {
    if (!isAuthenticated) return;
    // The API Token Source (pages/frontend/api-credential.html): every query_clj asks it (src/api.tsx
    // queryOmega); the Auth0 SDK serves its cached token and renews it once expired. A renewed token also
    // replaces state.ui.authToken, which the AI connection (authTokenRef) and the volume block read.
    let latest: string | undefined;
    const source: ApiTokenSource = async (options) => {
      const token = await getAccessTokenSilently(options);
      if (token !== latest) {
        latest = token;
        setAuthToken(token);
      }
      return token;
    };
    setApiTokenSource(source);
    source().catch((e) => console.error("FAILED_TO_FETCH_AUTH_TOKEN", e));
    return () => setApiTokenSource(null);
  }, [isAuthenticated]);
};

export const useInitialPageLoaded: () => [
  boolean,
  (initialPageLoaded: boolean) => void
] = () => {
  const initialPageLoaded = useSelector(
    (state: RootState) => state.ui.initialPageLoaded ?? false
  );
  const dispatch = useDispatch();
  const setInitialPageLoaded = (initialPageLoaded: boolean) => {
    dispatch({ type: ActionType.SET_INITIAL_PAGE_LOADING, initialPageLoaded });
  };
  return [initialPageLoaded, setInitialPageLoaded];
};

export const useSetInitialPage: () => void = () => {
  const { isAuthenticated } = useAuth0();
  const [selectedPage, setSelectedPage] = useSelectedPage();
  const [initialPageLoaded, setInitialPageLoaded] = useInitialPageLoaded();

  useEffect(() => {
    setInitialPageLoaded(true);
    if (isAuthenticated) {
      setSelectedPage({ contentType: PageContentType.LOADING_ACCOUNT });
    } else {
      setSelectedPage({ contentType: PageContentType.NOT_LOGGED_IN });
    }
  }, [initialPageLoaded]);
};

export const useDelete: () => (deletable: any) => Promise<any> = () =>
  deleteData;

export const useQuery = () => {
  const { search } = useLocation();
  return useMemo(
    () => Object.fromEntries(new URLSearchParams(search)),
    [search]
  );
};
